import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { Button } from '../../../base/components/Button';
import { Input } from '../../../base/components/Forms/Input';
import { VerificationCodeInput } from '../../../base/components/Forms/VerificationCodeInput';
import { Modal } from '../../../base/components/Modal';
import { __t } from '../../../base/utils/t';
import { PHONE_VERIFICATION_CODE_LENGTH, VERIFICATION_CODE_RESEND_TIMEOUT } from '../../units/authorization/constants';
import { PhoneInput } from '../../../base/components/Forms/PhoneInput';
import { CountDownTimer } from '../../../base/components/CountDownTimer';

import './styles.scss';

interface Props {
    confirmationMethod: 'email' | 'phone';
    text: ReactElement;
    codeValue: string;
    codeErrors?: string[];
    changeConfirmationValueErrors?: string[];
    updateModalText?: string;
    verificationCodeChangeHandler: (value: string) => void;
    submitResendCode: (recaptchaToken: string | null, callback: () => void) => void;
    submitChangeConfirmationValue: (newValue: string, recaptchaToken: string | null, callback: () => void) => void;
    editVerificationMethodValueHandler?: () => void;
    submitCode?: () => void;
}

export const Confirmation = ({
    confirmationMethod,
    text,
    codeValue,
    codeErrors,
    changeConfirmationValueErrors,
    updateModalText,
    verificationCodeChangeHandler,
    submitResendCode,
    submitChangeConfirmationValue,
    submitCode,
    editVerificationMethodValueHandler,
}: Props) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [newValue, setNewValue] = useState('');
    const [resetTimer, setResetTimer] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const [renderTimer, setRenderTimer] = useState(false);
    const [isTimerActive, setIsTimerActive] = useState(true);

    useEffect(() => {
        if (!window.GLOBALS.recaptchaSiteId) {
            console.error('recaptchaSiteId was not found!');
            return;
        }

        resendWithReCAPTCHA();

        return () => {
            verificationCodeChangeHandler('');
        };
    }, []);

    useEffect(() => {
        if (resetTimer) {
            setRenderTimer(false);
            setTimeout(() => {
                setRenderTimer(true);
                setIsTimerActive(true);
                setResetTimer(false);
            }, 1000);
        }
    }, [resetTimer]);

    useEffect(() => {
        if (!showEditModal) {
            setNewValue('');
        }
    }, [showEditModal]);

    const editClickHandler = () => {
        if (editVerificationMethodValueHandler) {
            editVerificationMethodValueHandler();
            return;
        }

        setShowEditModal(true);
    };

    const inputEditHandler = (value: string) => {
        setNewValue(value);
    };

    const submitChangeHandler = async () => {
        if (recaptchaRef.current) {
            const recaptchaToken = await recaptchaRef.current.executeAsync();
            recaptchaRef.current.reset();
            submitChangeConfirmationValue(newValue, recaptchaToken, () => {
                setShowEditModal(false);
                verificationCodeChangeHandler('');
                setResetTimer(true);
            });
        }
    };

    const resendWithReCAPTCHA = async () => {
        if (recaptchaRef.current) {
            const recaptchaToken = await recaptchaRef.current.executeAsync();
            recaptchaRef.current.reset();
            submitResendCode(recaptchaToken, () => {
                setResetTimer(true);
            });
        }
    };

    const timerExpirationCallback = () => setIsTimerActive(false);

    const timer = useMemo(() => {
        if (!renderTimer) {
            return null;
        }

        if (isTimerActive) {
            return (
                <p className='code-confirmation__resend-timer-message'>
                    {__t('Отправить код повторно можно через')}&nbsp;
                    <CountDownTimer expiresIn={VERIFICATION_CODE_RESEND_TIMEOUT} callback={timerExpirationCallback} />
                    &nbsp;{__t('сек')}
                </p>
            );
        }

        return (
            <Button type='link' className='code-confirmation__resend-timer-button' onClick={resendWithReCAPTCHA}>
                {__t('Отправить код еще раз')}
            </Button>
        );
    }, [renderTimer, isTimerActive, timerExpirationCallback, resendWithReCAPTCHA]);

    return (
        <div className='code-confirmation'>
            <p className='code-confirmation__text'>{text}</p>
            <div className='code-confirmation__verification-code-input'>
                <VerificationCodeInput
                    value={codeValue}
                    validChars='0-9'
                    length={PHONE_VERIFICATION_CODE_LENGTH}
                    errors={codeErrors}
                    onChange={verificationCodeChangeHandler}
                />
                <div className='code-confirmation__resend-timer'>{timer}</div>
            </div>
            {submitCode && (
                <Button className='code-confirmation__submit-button' onClick={submitCode}>
                    {__t('Отправить')}
                </Button>
            )}
            <div className='code-confirmation__update-link' onClick={editClickHandler}>
                {confirmationMethod === 'email' ? __t('Редактировать email') : __t('Изменить номер телефона')}
            </div>
            <Modal
                visible={showEditModal}
                visibilityHandler={() => setShowEditModal(false)}
                className='code-confirmation__update-modal'
            >
                <h4 className='code-confirmation__update-modal-title'>
                    {confirmationMethod === 'email' ? __t('Укажите ваш email') : __t('Укажите ваш телефон')}
                </h4>
                <p className='code-confirmation__update-modal-text'>{updateModalText}</p>
                {confirmationMethod === 'email' ? (
                    <Input
                        value={newValue}
                        placeholder={__t('Введите адрес почты')}
                        className='code-confirmation__update-modal-input'
                        onChange={inputEditHandler}
                        errors={changeConfirmationValueErrors}
                    />
                ) : (
                    <PhoneInput
                        value={newValue}
                        onChange={inputEditHandler}
                        className='code-confirmation__update-modal-input'
                        errors={changeConfirmationValueErrors}
                    />
                )}

                <Button onClick={submitChangeHandler} className='code-confirmation__update-modal-button'>
                    {__t('Отправить код')}
                </Button>
            </Modal>

            {window.GLOBALS.recaptchaSiteId && (
                <>
                <ReCAPTCHA ref={recaptchaRef} size='invisible' sitekey={window.GLOBALS.recaptchaSiteId} />
                </>
            )}
        </div>
    );
};
